import type { PatientDocumentAnswer } from "./answerFormat";

// Multi-answer saves of the inline editors (a chart row removal, a parentAnswer child + its
// parent, a customSignature + its name). Writes run one after the other through the single
// `/answer` endpoint and stop at the first failure; the error says which write failed and which
// are left, so Retry continues from there instead of starting over.
// Pure (no React / app runtime) so it is node-tested.

/** Answers outside a chart are stored on chart row -1 (as the classic form and mobile save them). */
export const NO_CHART_ROW = -1;

export interface AnswerWrite {
  /** `null` deletes the (question, chartRow) answer; `""` is stored as a value. */
  answer: PatientDocumentAnswer;
  chartRow: number;
  questionId: number;
}

export class SaveAnswersError extends Error {
  /** The original failure (shown to the reviewer). */
  readonly failure: unknown;
  /** Index in the requested writes of the one that failed. */
  readonly failedIndex: number;
  /** The failed write and every write after it: what Retry must send. */
  readonly remaining: AnswerWrite[];

  constructor(failure: unknown, failedIndex: number, remaining: AnswerWrite[]) {
    super(`Saving answer ${failedIndex + 1} failed`);
    this.name = "SaveAnswersError";
    this.failure = failure;
    this.failedIndex = failedIndex;
    this.remaining = remaining;
  }
}

/**
 * Runs `write` for each answer in order. Resolves with the number of writes done; on the first
 * failure stops and rejects with a SaveAnswersError (`onWritten` ran for every earlier write).
 */
export async function runSequentialWrites(
  writes: AnswerWrite[],
  write: (answer: AnswerWrite) => Promise<unknown>,
  onWritten: (answer: AnswerWrite) => void = () => undefined
): Promise<number> {
  for (let index = 0; index < writes.length; index++) {
    try {
      await write(writes[index]);
    } catch (error) {
      throw new SaveAnswersError(error, index, writes.slice(index));
    }
    onWritten(writes[index]);
  }
  return writes.length;
}

/** What Retry sends after `error`: the writes left by a SaveAnswersError, else all of them. */
export function writesToRetry(error: unknown, writes: AnswerWrite[]): AnswerWrite[] {
  return error instanceof SaveAnswersError ? error.remaining : writes;
}

/** What a save sends: the writes left by the last failure (`pending`), else the planned ones. */
export function writesToSend(pending: AnswerWrite[] | null, planned: AnswerWrite[]): AnswerWrite[] {
  return pending ?? planned;
}

/**
 * Whether Save / Retry is enabled. Writes left by a partial failure keep it enabled even when the
 * refreshed answers make the draft look unchanged (e.g. the child was saved, its parent was not).
 */
export function canSubmitWrites(canSave: boolean, pending: AnswerWrite[] | null): boolean {
  return canSave || (pending !== null && pending.length > 0);
}
